<template>
  <div v-if="modelValue" class="modal-overlay" @click.self="close">
    <div class="modal-card">
      <div class="section-header">
        <h3>{{ mode === 'edit' ? 'Edit Contact' : 'New Contact' }}</h3>
        <button class="icon-button" type="button" title="Close" @click="close">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <p v-if="error" class="banner banner-error">{{ error }}</p>

        <label class="modal-field">
          <span>Name *</span>
          <input v-model="draft.contact_name" type="text" placeholder="Jane Doe" />
        </label>
        <label class="modal-field">
          <span>Email</span>
          <input v-model="draft.email" type="email" placeholder="jane@example.com" />
        </label>
        <label class="modal-field">
          <span>Phone</span>
          <input v-model="draft.phone" type="text" placeholder="(555) 123-4567" />
        </label>
        <label class="modal-field">
          <span>Address</span>
          <input v-model="draft.address" type="text" placeholder="123 Main St" />
        </label>
        <label class="modal-field">
          <span>City</span>
          <input v-model="draft.city" type="text" />
        </label>
        <label class="modal-field">
          <span>Province</span>
          <input v-model="draft.province" type="text" />
        </label>
        <label class="modal-field">
          <span>Postal Code</span>
          <input v-model="draft.postal_code" type="text" />
        </label>
        <label class="modal-field">
          <span>Notes</span>
          <textarea v-model="draft.notes" rows="3" placeholder="Optional"></textarea>
        </label>
      </div>

      <div class="modal-actions">
        <button class="icon-button" type="button" @click="close">Cancel</button>
        <button
          class="send-button"
          type="button"
          :disabled="!draft.contact_name.trim() || sending"
          @click="save"
        >
          {{ sending ? (mode === 'edit' ? 'Saving…' : 'Creating…') : (mode === 'edit' ? 'Save changes' : 'Create contact') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  mode: { type: String, required: true }, // 'create' | 'edit'
  contact: { type: Object, default: null },
  companyId: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'saved', 'duplicate'])

function blankDraft() {
  return { contact_name: '', email: '', phone: '', address: '', city: '', province: '', postal_code: '', notes: '' }
}

const draft = ref(blankDraft())
const sending = ref(false)
const error = ref('')

watch(
  () => props.modelValue,
  (open) => {
    if (!open) return

    error.value = ''

    if (props.mode === 'edit' && props.contact) {
      draft.value = {
        contact_name: props.contact.contact_name || '',
        email: props.contact.email || '',
        phone: props.contact.phone || '',
        address: props.contact.address || '',
        city: props.contact.city || '',
        province: props.contact.province || '',
        postal_code: props.contact.postal_code || '',
        notes: props.contact.notes || '',
      }
    } else {
      draft.value = blankDraft()
    }
  }
)

function close() {
  emit('update:modelValue', false)
}

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function save() {
  const name = draft.value.contact_name.trim()

  if (!name || sending.value) return

  sending.value = true
  error.value = ''

  const isEdit = props.mode === 'edit'

  try {
    const headers = await authHeaders()
    const url = isEdit ? `${API_BASE_URL}/contacts/${props.contact.id}` : `${API_BASE_URL}/contacts`
    const method = isEdit ? 'PATCH' : 'POST'
    const body = isEdit
      ? {
          contact_name: name,
          email: draft.value.email.trim(),
          phone: draft.value.phone.trim(),
          address: draft.value.address.trim(),
          city: draft.value.city.trim(),
          province: draft.value.province.trim(),
          postal_code: draft.value.postal_code.trim(),
          notes: draft.value.notes.trim(),
        }
      : {
          company_id: props.companyId,
          contact_name: name,
          email: draft.value.email.trim() || undefined,
          phone: draft.value.phone.trim() || undefined,
          address: draft.value.address.trim() || undefined,
          city: draft.value.city.trim() || undefined,
          province: draft.value.province.trim() || undefined,
          postal_code: draft.value.postal_code.trim() || undefined,
          notes: draft.value.notes.trim() || undefined,
        }

    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
    })
    const data = await response.json().catch(() => ({}))

    if (response.status === 409 && data.existing_id) {
      error.value = data.error || 'A contact with this email already exists.'
      emit('duplicate', { existingId: data.existing_id })
      return
    }

    if (!response.ok || !data.ok) {
      throw new Error(data.error || `Failed to ${isEdit ? 'update' : 'create'} contact.`)
    }

    const token = await getAuthToken()
    logAction(token, {
      action: isEdit ? 'contact.update' : 'contact.create',
      label: isEdit ? `Updated contact ${data.contact.contact_name}` : `Created contact ${data.contact.contact_name}`,
      company_id: props.companyId,
      metadata: { contact_id: data.contact.id },
    })

    emit('saved', data.contact)
    emit('update:modelValue', false)
  } catch (err) {
    error.value = err.message || 'Something went wrong.'
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.banner {
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.icon-button:hover {
  color: #1f5c4d;
  border-color: #9fc8ba;
  background: #f3faf7;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 800;
}

.send-button {
  min-width: 130px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  align-self: end;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.send-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(23, 35, 31, 0.45);
}

.modal-card {
  width: min(440px, 100%);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 24px 60px rgba(23, 35, 31, 0.25);
}

.modal-body {
  display: grid;
  gap: 12px;
  padding: 16px;
  overflow: auto;
}

.modal-field {
  display: grid;
  gap: 5px;
}

.modal-field span {
  color: var(--text-secondary);
  font-size: 0.76rem;
  font-weight: 750;
  text-transform: uppercase;
}

.modal-field input,
.modal-field select {
  height: 40px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 0 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.modal-field textarea {
  width: 100%;
  min-width: 0;
  resize: vertical;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 11px 12px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
  outline: none;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid var(--border-subtle);
}
</style>
